import type {
  GridProps
} from '@chakra-ui/react'
import {
  Box,
  Divider,
  Grid,
  Spinner,
  Text
} from '@chakra-ui/react'
import type { Offer, OfferAddon } from 'BrokerTsLib/src/dtos'
import { Insurer, Selection } from 'BrokerTsLib/src/enums'
import { priceFormat } from 'BrokerTsLib/src/Utils/formatPrice'
import { useRef, useState } from 'react'

import { CompanyLogo } from '@/components/CompanyLogo'
import { CompletedOverview } from '@/components/CompletedOverview'
import type { CompletedOverviewItemProps } from '@/components/CompletedOverview/CompletedOverviewItem'
import { RecapSummary } from '@/components/RecapSummary'
import {
  RecapSidebar,
  SectionItem,
} from '@/components/RecapSummary/RecapSidebar'
import { RecapTable } from '@/components/RecapTable'
import type { RecapTableProps } from '@/components/RecapTable/RecapTable'
import { DateSelector } from '@/containers/dashboard/Stats/DateSelector'
import { useGetInsurerInfo } from '@/hooks'

const InformationTab = {
  Recap: 0,
  Info: 1,
  AdditionalDates: 2,
}
export type InformationTab = typeof InformationTab[keyof typeof InformationTab]

type InformationProps = {
  number?: number | React.ReactNode
  sectionTitle?: string
  offer: Offer
  recapProps: RecapTableProps
  completedOverviewData: CompletedOverviewItemProps[]
  completedOverviewProps?: GridProps
  additionalInfoData?: CompletedOverviewItemProps[]
  additionalInfoProps?: GridProps
  sidebarSectionItems?: React.ReactNode[]
  additionalDatesTabData?: CompletedOverviewItemProps[]
  additionalDatesTabProps?: GridProps
}

const getTotalPriceName = (offer: Offer) => {

  if ([Insurer.Colonnade, Insurer.Fortegra].includes(offer.insurerId)) {
    const addonOffer = offer as OfferAddon
    return offer.paymentFrequencyId == Selection.OneTime ? 'Celkem'
      : addonOffer.price > (addonOffer.pricePerYear ?? addonOffer.price) ? 'Celkové pojistné' // Když je pojistka na několik let a platí se ročně.
        : 'Roční pojistné'
  }

  return offer.paymentFrequencyId == Selection.OneTime ? 'Celkem' : 'Roční pojistné'
}

export const Information = ({
  number,
  sectionTitle,
  offer,
  recapProps,
  completedOverviewData,
  additionalInfoData,
  completedOverviewProps,
  additionalInfoProps,
  sidebarSectionItems,
  additionalDatesTabData,
  additionalDatesTabProps,
}: InformationProps) => {
  const completedOverviewRef = useRef<HTMLDivElement>(null)

  const { data: dataInsurer, isLoading: isLoadingInsurer } = useGetInsurerInfo(
    offer.insurerId,
    { retry: false },
  )
  const [selectedTab, setSetselectedTab] = useState<InformationTab>(
    InformationTab.Info,
  )

  const tabs = [
    { name: 'Informace', value: InformationTab.Info },
    { name: 'Rekapitulace', value: InformationTab.Recap },
  ]

  if (additionalDatesTabData && additionalDatesTabData.length > 0) {
    tabs.push({
      name: 'Doplňující datumy',
      value: InformationTab.AdditionalDates,
    })
  }

  return (
    <RecapSummary
      number={number}
      offer={offer}
      sectionTitle={sectionTitle}
      wrapperProps={{
        sx: {
          gridTemplateRows: { lg: 'auto 1fr' },
        },
      }}
      bodyProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 8,
        },
      }}
      sidebar={
        <RecapSidebar
          logo={
            <CompanyLogo
              insurer={{ id: offer.insurerId, name: offer.insurerName }}
              p={8}
              maxW='240px'
              offerType={offer.type}
            />
          }
          website={isLoadingInsurer ? <Spinner /> : dataInsurer?.data?.url}
          email={isLoadingInsurer ? <Spinner /> : dataInsurer?.data?.email}
          paymentFrequencyId={offer.paymentFrequencyId!}
          paymentPrice={offer.priceFrequency}
          totalPrice={offer.price}
          completed={true}
          sectionItems={[
            <SectionItem label={getTotalPriceName(offer)}>
              <Text sx={{ fontSize: 'lg', fontWeight: 'bold' }}>
                {priceFormat(offer.price)}
              </Text>
            </SectionItem>,
            ...(sidebarSectionItems ?? []),
          ]}
        />
      }
    >
      {selectedTab == InformationTab.Info && (
        <>
          <CompletedOverview
            ref={completedOverviewRef}
            items={completedOverviewData}
            {...completedOverviewProps}
          />

          {additionalInfoData &&
            additionalInfoData.length > 0 &&
            completedOverviewData &&
            completedOverviewData.length > 0 && <Divider />}

          <CompletedOverview
            items={additionalInfoData}
            sx={{
              flex: '1',
              // h: '100%',
              // gridTemplateColumns: { base: '1fr', md: '1fr' },
            }}
            {...additionalInfoProps}
          />
        </>
      )}

      {selectedTab == InformationTab.Recap && (
        <Box
          sx={{
            pl: {
              base: 0,
              md: 16,
            },
          }}
        >
          <RecapTable {...recapProps} />
        </Box>
      )}

      {selectedTab == InformationTab.AdditionalDates && (
        <CompletedOverview
          //ref={completedOverviewRef}
          items={additionalDatesTabData}
          {...additionalDatesTabProps}
        />
      )}

      <Divider sx={{ alignSelf: 'end', justifySelf: 'end', mt: 'auto' }} />

      <Grid>
        <DateSelector
          sx={{ mx: 'auto' }}
          options={tabs}
          value={selectedTab}
          onChange={(value) => setSetselectedTab(value)}
        />
      </Grid>
    </RecapSummary>
  )
}
